import {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledInput from './StyledInput';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMemo } from 'react';

interface StyledTranslatableInputProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  name: string; // e.g., "shortDescription"
  originalName: Path<T>;
  setValue: UseFormSetValue<T>;
  language: SupportedLocale;
  title?: string;
  placeholder?: string;
  disabled?: boolean;
  type?: string;
  className?: string;
}

const StyledTranslatableInput = <T extends FieldValues = FieldValues>({
  control,
  name,
  originalName,
  setValue,
  language,
  title,
  placeholder,
  disabled,
  type,
  className,
}: StyledTranslatableInputProps<T>) => {
  // Use a computed field name that includes the language
  const translationFieldName = `translations.${language}.${name}`;
  const { currentLanguage } = useLanguage();

  const onChange = useMemo(
    () =>
      language === currentLanguage
        ? (event: React.ChangeEvent<HTMLInputElement>) => {
            setValue(
              originalName,
              event.target.value as PathValue<T, Path<T>>,
              {
                shouldDirty: true,
              },
            );
          }
        : undefined,
    [language, currentLanguage, originalName, setValue],
  );

  return (
    <StyledInput
      key={`${name}-${language}`}
      control={control}
      name={translationFieldName as Path<T>}
      onChange={onChange}
      title={title}
      placeholder={placeholder}
      disabled={disabled}
      type={type}
      className={className}
    />
  );
};

export default StyledTranslatableInput;
